import { practices } from '../data/practices'
import { Practice, PracticeDraft } from '../types/practice'

type Listener = () => void

class PracticesStore {
  private practices: Practice[] = [...practices]
  // Course codes inserted from the practices, on top of existing courses
  private extraCourseCodes: string[] = []
  private listeners = new Set<Listener>()

  getAll(): Practice[] {
    return this.practices
  }

  getExtraCourseCodes(): string[] {
    return this.extraCourseCodes
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit() {
    this.listeners.forEach((l) => l())
  }

  save(draft: PracticeDraft): Practice {
    const practice = { ...draft, id: draft.id || `prt-${Date.now()}` } as Practice
    const exists = this.practices.some((p) => p.id === practice.id)
    this.practices = exists
      ? this.practices.map((p) => (p.id === practice.id ? practice : p))
      : [practice, ...this.practices]
    this.emit()
    return practice
  }

  update(id: string, changes: Partial<Practice>) {
    this.practices = this.practices.map((p) =>
      p.id === id ? { ...p, ...changes } : p,
    )
    this.emit()
  }

  // Records a sent email in the "Storico e azioni" of each practice
  logEmail(ids: string[], recipients: Record<string, string[]>, body: string) {
    const now = new Date().toISOString()
    this.practices = this.practices.map((p) =>
      ids.includes(p.id)
        ? {
            ...p,
            history: [
              ...p.history,
              {
                id: `h-${Date.now()}-${p.id}`,
                kind: 'email' as const,
                notes: `Email inviata a: ${(recipients[p.id] || []).join(', ')}\n\n${body}`,
                createdAt: now,
              },
            ],
          }
        : p,
    )
    this.emit()
  }

  remove(id: string) {
    this.practices = this.practices.filter((p) => p.id !== id)
    this.emit()
  }

  addCourseCode(code: string): string {
    const trimmed = code.trim()
    if (!this.extraCourseCodes.includes(trimmed)) {
      this.extraCourseCodes = [...this.extraCourseCodes, trimmed]
      this.emit()
    }
    return trimmed
  }
}

export const practicesStore = new PracticesStore()
